import { forwardRef, useState } from "react";
import DatePicker, {
  CalendarContainer,
  registerLocale,
} from "react-datepicker";
import { enUS, lt } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { CalendarIcon } from "@/assets/icons";
import FormError from "./FormError";
import "react-datepicker/dist/react-datepicker.css";
import "@/styles/datepicker-overrides.css";

registerLocale("lt", lt);
registerLocale("en", enUS);

interface FormDatePickerProps {
  label?: string;
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  onBlur?: () => void;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  error?: string;
  errorParams?: Record<string, string | number>;
}

const CustomInput = forwardRef<
  HTMLInputElement,
  React.HTMLProps<HTMLInputElement> & {
    error?: string;
  }
>(({ className, error, ...props }, ref) => (
  <div className="relative">
    <input
      ref={ref}
      {...props}
      className={`
        w-full py-2 text-sm bg-white border rounded-md
        focus:outline-none transition-colors duration-200
        ${props.readOnly ? "px-3" : "pl-3 pr-10"}
        ${
          props.readOnly
            ? "border-gray-200 bg-gray-50 focus:border-gray-200 focus:ring-0 focus:outline-none"
            : error
              ? "border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              : "border-gray-300 hover:border-gray-400 focus:ring-2 focus:ring-primary focus:border-primary"
        }
        ${props.disabled ? "opacity-50 cursor-not-allowed bg-gray-50" : "cursor-text"}
        ${props.readOnly ? "cursor-default" : ""}
        ${className || ""}
      `}
    />
    {!props.readOnly && (
      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
        <CalendarIcon className="text-gray-400" size={18} />
      </div>
    )}
  </div>
));

CustomInput.displayName = "CustomInput";

export default function FormDatePicker({
  label,
  value,
  onChange,
  onBlur,
  className = "",
  disabled = false,
  readOnly = false,
  required = false,
  error,
  errorParams,
}: FormDatePickerProps) {
  const { i18n, t } = useTranslation();

  const [open, setOpen] = useState(false);

  const selectedDate = value ? new Date(value) : null;

  const [tempDate, setTempDate] = useState<Date | null>(selectedDate);

  const currentLocale = i18n.language?.startsWith("en") ? "en" : "lt";

  const handleOpen = () => {
    setTempDate(selectedDate);
    setOpen(true);
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-black mb-2">
          {label}
          {required && <span className="ml-1">*</span>}
        </label>
      )}

      <DatePicker
        open={open}
        onInputClick={handleOpen}
        onClickOutside={() => setOpen(false)}
        selected={selectedDate}
        onChange={(date: Date | null) => setTempDate(date)}
        onBlur={readOnly ? undefined : onBlur}
        locale={currentLocale}
        dateFormat="yyyy-MM-dd"
        disabled={disabled}
        readOnly={readOnly}
        placeholderText="YYYY-MM-DD"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        customInput={<CustomInput error={error} />}
        wrapperClassName="w-full"
        calendarClassName="custom-calendar"
        calendarContainer={({ className, children }) => (
          <div className={`${className} datepicker-container`}>
            <CalendarContainer>
              {children}
              <div className="datepicker-footer">
                <button
                  type="button"
                  className="datepicker-confirm-btn"
                  onClick={() => {
                    if (tempDate) {
                      const year = tempDate.getFullYear();
                      const month = String(tempDate.getMonth() + 1).padStart(
                        2,
                        "0"
                      );
                      const day = String(tempDate.getDate()).padStart(2, "0");
                      onChange(`${year}-${month}-${day}`);
                    } else {
                      onChange(null);
                    }
                    setOpen(false);
                  }}
                >
                  {t("datePicker.confirm")}
                </button>

                <button
                  type="button"
                  className="datepicker-cancel-btn"
                  onClick={() => {
                    setTempDate(selectedDate);
                    setOpen(false);
                  }}
                >
                  {t("datePicker.cancel")}
                </button>
              </div>
            </CalendarContainer>
          </div>
        )}
        renderCustomHeader={({
          date,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
          changeYear,
          changeMonth,
        }) => (
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              type="button"
              className="bg-white datepicker-nav-btn"
            >
              <span className="bg-white datepicker-nav-icon datepicker-nav-icon-prev"></span>
            </button>

            <div className="flex gap-2">
              <select
                value={date.getMonth()}
                onChange={({ target: { value } }) => changeMonth(Number(value))}
                className="datepicker-select"
              >
                {lt.localize?.month
                  ? Array.from({ length: 12 }).map((_, i) => (
                      <option key={i} value={i}>
                        {lt.localize.month(i, { width: "abbreviated" })}
                      </option>
                    ))
                  : null}
              </select>

              <select
                value={date.getFullYear()}
                onChange={({ target: { value } }) => changeYear(Number(value))}
                className="datepicker-select"
              >
                {Array.from({ length: 20 }, (_, i) => {
                  const year = 2020 + i;

                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>

            <button
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              type="button"
              className="bg-white datepicker-nav-btn"
            >
              <span className="bg-white datepicker-nav-icon datepicker-nav-icon-next"></span>
            </button>
          </div>
        )}
      />

      <FormError error={error} params={errorParams} />
    </div>
  );
}
